import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"

import { imageFilesFromDataTransfer } from "../../pages/PortalBlogPosts/blogInlineImage"

import { VALIDATION_ERROR_INPUT_CLASSNAMES, ValidationError } from "./Inputs"

import "react-quill/dist/quill.snow.css"
import "./wysiwyg.sass"

export const DEFAULT_TOOLBAR_OPTIONS = [
  [{ header: [false, 2, 3, 4] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["bold", "italic", "underline"],
  ["link"],
  [{ align: [] }]
]

export const BUILDER_V2_TOOLBAR_OPTIONS = [
  [{ header: [false, 3] }],
  [{ list: "ordered" }, { list: "bullet" }],
  ["bold", "italic", "underline"],
  ["link"],
  [{ align: [] }]
]

export const BUILDER_V2_HEADER_LABELS = {
  default: "Normal",
  3: "Sub-header"
}

export const QuillEditor = forwardRef(
  (
    {
      value,
      onChange,
      onChangeWithDelta,
      toolbarOptions = DEFAULT_TOOLBAR_OPTIONS,
      headerLabelOverrides,
      modules: modulesProp,
      className,
      validationError,
      enableImages = false,
      onRequestImage,
      onImageFiles,
      ...props
    },
    ref
  ) => {
    const [ReactQuill, setReactQuill] = useState(null)
    const quillRef = useRef(null)
    const [internalValue, setInternalValue] = useState(value || "")
    const onRequestImageRef = useRef(onRequestImage)
    const onImageFilesRef = useRef(onImageFiles)

    onRequestImageRef.current = onRequestImage
    onImageFilesRef.current = onImageFiles

    useImperativeHandle(ref, () => ({
      insertImage(url) {
        const quill = quillRef.current?.getEditor()
        if (!quill || !url) return
        const range = quill.getSelection(true) || { index: quill.getLength() }
        quill.insertEmbed(range.index, "image", url, "user")
        quill.setSelection(range.index + 1, 0, "user")
      }
    }))

    useEffect(() => {
      import("react-quill")
        .then((module) => {
          setReactQuill(() => module.default)
        })
        .catch((error) => console.error("Error importing ReactQuill:", error)) // eslint-disable-line no-console
    }, [])

    // Update internal value when external value changes
    useEffect(() => {
      if (value !== undefined && value !== internalValue) {
        setInternalValue(value)
      }
    }, [value, internalValue])

    useEffect(() => {
      if (!headerLabelOverrides || !quillRef.current) {
        return
      }

      const container = quillRef.current?.container
      if (!container) {
        return
      }

      const applyHeaderLabels = () => {
        const headerPicker = container.querySelector(".ql-header")
        if (!headerPicker) {
          return
        }

        const options = headerPicker.querySelector(".ql-picker-options")
        if (options) {
          Object.entries(headerLabelOverrides).forEach(([valueKey, label]) => {
            const selector =
              valueKey === "default" ? ".ql-picker-item:not([data-value])" : `.ql-picker-item[data-value='${valueKey}']`
            const option = options.querySelector(selector)
            if (option) {
              option.setAttribute("data-label", label)
              option.textContent = label
            }
          })
        }

        const labelEl = headerPicker.querySelector(".ql-picker-label")
        if (labelEl) {
          const currentValue = labelEl.getAttribute("data-value") || "default"
          const label = headerLabelOverrides[currentValue] || headerLabelOverrides.default
          if (label) {
            labelEl.setAttribute("data-label", label)
            labelEl.textContent = label
          }
        }
      }

      applyHeaderLabels()

      const observer = new MutationObserver(applyHeaderLabels)
      observer.observe(container, { childList: true, subtree: true })

      return () => observer.disconnect()
    }, [headerLabelOverrides, ReactQuill])

    useEffect(() => {
      if (ReactQuill && quillRef.current) {
        const quillInstance = quillRef.current.getEditor()

        const updateTooltipPosition = () => {
          const tooltip = document.querySelector(".ql-tooltip")
          if (tooltip && tooltip.style.left && Number(tooltip.style.left.replace("px", "")) < 0) {
            tooltip.style.left = 0
          }
        }

        quillInstance.on("selection-change", updateTooltipPosition)

        return () => {
          quillInstance.off("selection-change", updateTooltipPosition)
        }
      }
    }, [ReactQuill])

    useEffect(() => {
      if (!enableImages || !ReactQuill || !quillRef.current) return

      const root = quillRef.current.getEditor().root

      const handleImageTransfer = (event) => {
        const files = imageFilesFromDataTransfer(event.clipboardData || event.dataTransfer)
        if (!files.length) return
        event.preventDefault()
        event.stopPropagation()
        onImageFilesRef.current?.(files)
      }

      const handleDragOver = (event) => {
        const files = imageFilesFromDataTransfer(event.dataTransfer)
        if (!files.length) return
        event.preventDefault()
      }

      root.addEventListener("paste", handleImageTransfer, true)
      root.addEventListener("drop", handleImageTransfer, true)
      root.addEventListener("dragover", handleDragOver)

      return () => {
        root.removeEventListener("paste", handleImageTransfer, true)
        root.removeEventListener("drop", handleImageTransfer, true)
        root.removeEventListener("dragover", handleDragOver)
      }
    }, [enableImages, ReactQuill])

    const imageHandler = useCallback(() => {
      onRequestImageRef.current?.()
    }, [])

    const resolvedModules = useMemo(() => {
      if (modulesProp) return modulesProp
      if (!enableImages) return { toolbar: toolbarOptions }

      return {
        toolbar: {
          container: [...toolbarOptions, ["image"]],
          handlers: {
            image: imageHandler
          }
        }
      }
    }, [modulesProp, toolbarOptions, enableImages, imageHandler])

    const handleChange = (content, delta, source, editor) => {
      const htmlContent = editor.getHTML()
      const deltaContent = editor.getContents()
      setInternalValue(htmlContent)

      if (onChange) {
        onChange(htmlContent)
      }

      if (onChangeWithDelta) {
        onChangeWithDelta({
          html: htmlContent,
          delta: deltaContent
        })
      }
    }

    if (!ReactQuill) {
      return <div>Loading editor...</div>
    }

    return (
      <div>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={internalValue}
          onChange={handleChange}
          className={twMerge("w-full", className, validationError && VALIDATION_ERROR_INPUT_CLASSNAMES)}
          {...props}
          modules={resolvedModules}
        />
        {validationError && !!validationError.length && <ValidationError>{validationError}</ValidationError>}
      </div>
    )
  }
)

QuillEditor.displayName = "QuillEditor"

export default QuillEditor
